import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className={`faq-accordion-item ${isOpen ? 'open' : ''}`}>
      <button className="faq-question" onClick={onClick}>
        <span>{question}</span>
        <span className="faq-icon">{isOpen ? '−' : '+'}</span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="faq-answer-wrapper"
          >
            <div className="faq-answer">
              <p>{answer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: "Will color palettes transfer between projects?",
      answer: "Yes! Color palettes will transfer between projects. Simply open the ColorCache extension in the new project and all your palettes will be available."
    },
    {
      question: "Can I share palettes with my team?",
      answer: "Yes! You can export individual palettes by clicking the 3 dots next to a palette name and selecting \"Export Palette\". To share everything at once, use the \"Bulk Share Palettes\" button in the footer, which will generate individual ZIP files for every palette in your collection. Your teammates can then import these ZIP files by clicking \"Import Palette(s)\" in their ColorCache footer."
    },
    {
      question: "Am I the right editor for this extension?",
      answer: "ColorCache is best for people who check one of the following boxes: editor within an agency, edit for multiple brands, edit for multiple sports teams, edit for multiple clients, or are a freelancer. "
    },
    {
      question: "Does it work on both Mac and Windows?",
      answer: "Yes! ColorCache is fully compatible with both macOS and Windows. As long as you're running a supported version of Adobe Premiere Pro (v23-25), you're good to go."
    }
  ];

  return (
    <section className="faq-section">
      <div className="container">
        <div className="faq-header text-center">
          <span className="showcase-badge">Common Questions</span>
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="showcase-description">
            Everything you need to know about getting started with ColorCache.
          </p>
        </div>

        <div className="faq-accordion">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;

